/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  FlexContainer,
  ModalConfirm,
  Row,
  Tab,
  Tabs,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  useGetUsersPermissionsQuery,
  useRemoveUserPermissionsMutation,
  UserPermissions,
} from "~/generated/graphql";
import {
  BreadcrumbsBox,
  ErrorWrap,
  GlobalSpinner,
  LoaderWrap,
  Pagination,
  ReactTable,
  TableLoader,
} from "~/components";
import { ERROR_TEXT } from "~/constants";
import { useDisclosure, useQueryState, useTabsWithUrl } from "~/hooks";
import { showError } from "~/services/toast";
import { generateActionConfirmText } from "~/tools";
import { ApiKeysTable } from "./GlobalApiKeys/ApiKeysTable";
import { AddUserPermissionsSidebar } from "./UsersAndPermissions/AddUserPermissionsSidebar";
import { UsersAndPermissionsColumns } from "./UsersAndPermissions/UsersAndPermissionsListColumns";

export function UsersAndPermissionsPage() {
  const {
    isOpen: isModalConfirmOpen,
    close: closeModalConfirm,
    open: openModalConfirm,
  } = useDisclosure();
  const [userEmail, setUserEmail] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [editItem, setEditItem] = useState<UserPermissions>();

  const { isOpen, close, open } = useDisclosure();
  const { setActiveTab } = useTabsWithUrl();

  const {
    state: { page, limit, tab },
    updateQueryState,
  } = useQueryState();

  const [removeUserPermissions, { loading: isRemoveUserPermissionsLoading }] =
    useRemoveUserPermissionsMutation();
  const { data, loading, refetch, error } = useGetUsersPermissionsQuery({
    variables: {
      limit,
      page,
    },
  });

  const onRemoveConfirmationModal = useCallback(
    (email: string) => {
      setUserEmail(email);
      openModalConfirm();
    },
    [setUserEmail, openModalConfirm]
  );

  const onRemoveUserPermissions = async (email: string) => {
    try {
      await removeUserPermissions({
        variables: {
          email,
        },
      });
      await refetch();
    } catch {
      showError(ERROR_TEXT.default);
    }
    closeModalConfirm();
    setUserEmail("");
  };

  const onAddNewUser = () => {
    setIsEdit(false);
    open();
  };

  const onEdit = useCallback(
    (item: UserPermissions) => {
      setIsEdit(true);
      setEditItem(item);
      open();
    },
    [setIsEdit, setEditItem, open]
  );

  const usersTableColumns = useMemo(
    () => UsersAndPermissionsColumns(onEdit, onRemoveConfirmationModal),
    [onEdit, onRemoveConfirmationModal]
  );

  useEffect(() => {
    updateQueryState({ page: 0 });
    // there is problem with `updateQueryState` it returns new reference every time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <>
      <GlobalSpinner loading={isRemoveUserPermissionsLoading} />
      <Row>
        <Col>
          <BreadcrumbsBox
            title="Users and Permissions"
            labels={[
              {
                value: "users-and-permissions",
                label: "Users and Permissions",
              },
            ]}
          />

          <ErrorWrap error={error}>
            <Tabs step={tab ? Number(tab) : 0} handleTab={setActiveTab}>
              <Tab
                label={
                  <Text weight="medium" size="lg" mb={theme.spacing.spacing01}>
                    Users and permissions
                  </Text>
                }
              >
                <FlexContainer justifyContent="flex-end">
                  <Button
                    style={{ marginBottom: theme.spacing.spacing01 }}
                    icon="plus"
                    onClick={onAddNewUser}
                  >
                    Add new User
                  </Button>
                </FlexContainer>
                <LoaderWrap loading={loading} Component={Loader}>
                  <ReactTable
                    striped
                    columns={usersTableColumns}
                    sort={false}
                    data={data?.usersPermissions?.usersPermissions ?? []}
                  />
                  <Pagination count={data?.usersPermissions?.count ?? 0} />
                </LoaderWrap>
              </Tab>
              <Tab
                label={
                  <Text weight="medium" size="lg" mb={theme.spacing.spacing01}>
                    Global API keys
                  </Text>
                }
              >
                <ApiKeysTable />
              </Tab>
            </Tabs>
          </ErrorWrap>
        </Col>
      </Row>
      <AddUserPermissionsSidebar
        isOpen={isOpen}
        refetch={refetch}
        edit={isEdit}
        editItem={editItem}
        onClose={close}
      />
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={() => onRemoveUserPermissions(userEmail)}
        actionLabel="Delete"
        contentLabel="Delete Permissions"
        onClose={closeModalConfirm}
      >
        {generateActionConfirmText(`permissions for ${userEmail}`)("delete")()}
      </ModalConfirm>
    </>
  );
}

function Loader() {
  return (
    <TableLoader
      gapY={10}
      gapX={10}
      css={{
        marginTop: theme.spacing.spacing04,
      }}
      rows={[
        {
          height: 20,
          count: 1,
          items: [
            {
              widthPercent: 44,
            },
            {
              widthPercent: 15,
            },
            {
              widthPercent: 35,
            },
            {
              widthPercent: 6,
            },
          ],
        },
        {
          height: 60,
          count: 9,
          items: [
            {
              widthPercent: 44,
            },
            {
              widthPercent: 15,
            },
            {
              widthPercent: 35,
            },
            {
              widthPercent: 6,
            },
          ],
        },
      ]}
    />
  );
}
